import type {
  BundlePreview,
  BundlePreviewError,
  CombinationSuggestion,
  EntryFilterImpact,
  ExitPlacementImpact,
  PairwiseRow,
  PickResult,
  PolicyPickOverlapAction,
  PolicyPickOverlapSimType,
  TradeSubsetMetrics,
} from "../../../types/policyPickOverlap";
import type { ExitPlacementLegAvailability } from "../../../utils/exitPlacementSimLegs";
import { formatExitPlacementLegsKindBadge } from "../../../utils/exitPlacementSimLegs";

export function formatOverlapMoney(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

export function formatOverlapPct(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

export function actionLabel(action: PolicyPickOverlapAction): string {
  return action === "keep_only" ? "Keep" : "Remove";
}

export function simTypeLabel(simType: PolicyPickOverlapSimType): string {
  if (simType === "exit_placement") return "Exit placement";
  if (simType === "keep") return "Keep";
  return "Remove";
}

/** Bottom-right kind badge: Remove / Keep / SL / TP / SL TP. */
export function overlapImpactKindBadgeLabel(
  pick: PickResult,
  exitAvailability?: ExitPlacementLegAvailability | null
): string {
  if (pick.sim_type === "exit_placement") {
    if (exitAvailability) return formatExitPlacementLegsKindBadge(exitAvailability);
    return "Exit placement";
  }
  return actionLabel(pick.action);
}

export function isExitPlacementImpact(
  impact: EntryFilterImpact | ExitPlacementImpact
): impact is ExitPlacementImpact {
  return "loss_improved_count" in impact;
}

export function isExitPlacementPickResult(result: PickResult): boolean {
  return result.sim_type === "exit_placement";
}

export function entryFilterImpactSummary(
  impact: EntryFilterImpact,
  action: PolicyPickOverlapAction
): string {
  if (action === "keep_only") {
    return `Drops ${impact.affected_trade_count} trades (${impact.affected_losses} losses, ${impact.affected_wins} wins) outside keep rule`;
  }
  return `Removes ${impact.affected_losses} losses, ${impact.affected_wins} wins`;
}

/** EP: exits moved (trades stay). Do not say “Removes”. */
export function exitPlacementImpactSummary(
  impact: EntryFilterImpact | ExitPlacementImpact
): string {
  return `Exits changed ${impact.affected_trade_count} trades (${impact.affected_losses} losses, ${impact.affected_wins} wins)`;
}

export function exitPlacementDeltaPnlLine(
  impact: EntryFilterImpact | ExitPlacementImpact
): string {
  return `Δ PnL ${formatOverlapMoney(impact.net_pnl_delta_if_applied)} (from this pick’s sim)`;
}

export function pickResultImpactSummary(result: PickResult): string {
  if (isExitPlacementPickResult(result)) {
    return exitPlacementImpactSummary(result.impact);
  }
  return entryFilterImpactSummary(result.impact as EntryFilterImpact, result.action);
}

export function pairwiseRowIsExitPlacement(row: PairwiseRow): boolean {
  return row.sim_type_a === "exit_placement" || row.sim_type_b === "exit_placement";
}

export function baselineSummaryLine(baseline: TradeSubsetMetrics): string {
  const wr =
    baseline.win_rate != null ? `${baseline.win_rate.toFixed(1)}% WR` : "— WR";
  return `${baseline.trade_count} trades · ${wr} · ${formatOverlapMoney(baseline.total_pnl)} PnL`;
}

export function overlapCellColor(overlapPct: number): string {
  if (overlapPct <= 0.25) return "rgba(6, 227, 99, 0.18)";
  if (overlapPct <= 0.5) return "rgba(56, 187, 209, 0.18)";
  if (overlapPct <= 0.75) return "rgba(244, 145, 19, 0.22)";
  return "rgba(224, 1, 1, 0.18)";
}

export function complementarityTooltip(scores: BundlePreview["scores"]): string {
  return [
    `Loss removal: ${formatOverlapPct(scores.loss_removal_ratio)}`,
    `Win removal: ${formatOverlapPct(scores.win_removal_ratio)}`,
    `Overlap penalty: ${formatOverlapPct(scores.overlap_penalty)}`,
    `Composite: ${scores.composite_score.toFixed(2)}`,
  ].join(" · ");
}

export function suggestionImpactLine(suggestion: CombinationSuggestion): string {
  const impact = suggestion.combined_impact;
  return `Removes ${impact.affected_losses} losses, ${impact.affected_wins} wins · Δ ${formatOverlapMoney(impact.net_pnl_delta_if_applied)}`;
}

/** Unique pick ids from the checked Top suggestions (stable first-seen order). */
export function unionPickIdsFromSuggestions(
  suggestions: CombinationSuggestion[],
  selectedRanks: ReadonlySet<number>
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const suggestion of suggestions) {
    if (!selectedRanks.has(suggestion.rank)) continue;
    for (const id of suggestion.pick_ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

export function bundlePreviewHasBlockingError(bundle: BundlePreview): boolean {
  return bundle.errors.some((e: BundlePreviewError) => e.code === "unsatisfiable_keep_and");
}

export function findBundlePreviewForPickIds(
  bundles: BundlePreview[],
  pickIds: string[]
): BundlePreview | undefined {
  const key = [...pickIds].sort().join("|");
  return bundles.find((b) => [...b.pick_ids].sort().join("|") === key);
}

export function pickLabelById(
  pickId: string,
  picks: PickResult[],
  fallback?: string
): string {
  const found = picks.find((p) => p.pick_id === pickId);
  return found?.label || fallback || pickId;
}

/** True when a run/card title is only an overlap placeholder (no policy text). */
export function isGenericOverlapRunTitle(raw: string): boolean {
  return /^Overlap\s+(suggestion\s+)?#\d+(\s*·\s*)?$/i.test(raw.trim());
}

/** Highest `Overlap #N` / `Overlap suggestion #N` found in titles (0 if none). */
export function maxOverlapSerialFromTitles(
  titles: Array<string | null | undefined>
): number {
  let max = 0;
  for (const raw of titles) {
    const title = raw?.trim();
    if (!title) continue;
    const match = title.match(/Overlap(?:\s+suggestion)?\s+#(\d+)/i);
    if (!match) continue;
    const n = Number(match[1]);
    if (Number.isFinite(n)) max = Math.max(max, n);
  }
  return max;
}

/** Next `count` serials after any existing Overlap #N titles on the board. */
export function allocateOverlapSerials(
  existingTitles: Array<string | null | undefined>,
  count: number
): number[] {
  if (count <= 0) return [];
  let next = maxOverlapSerialFromTitles(existingTitles) + 1;
  return Array.from({ length: count }, () => next++);
}

/** Rewrite `Overlap #N · …` (or bare Overlap #N) to use `serial`. */
export function withOverlapSerial(title: string, serial: number): string {
  const trimmed = title.trim();
  if (!trimmed) return `Overlap #${serial}`;
  if (/^Overlap(?:\s+suggestion)?\s+#\d+/i.test(trimmed)) {
    return trimmed.replace(/^Overlap(?:\s+suggestion)?\s+#\d+/i, `Overlap #${serial}`);
  }
  return `Overlap #${serial} · ${trimmed}`;
}

/**
 * Prefer a concrete policy label over a generic "Overlap suggestion #N" echo from the API
 * or a stored combined-run title.
 */
export function resolveOverlapPickDisplayLabel(
  apiLabel: string | undefined,
  selectionLabel: string | undefined
): string {
  const api = apiLabel?.trim() || "";
  const sel = selectionLabel?.trim() || "";
  if (api && !isGenericOverlapRunTitle(api)) return api;
  if (sel && !isGenericOverlapRunTitle(sel)) return sel;
  return api || sel || "Untitled policy";
}

type AffectedImpactCounts = {
  affected_trade_count: number;
  affected_wins: number;
  affected_losses: number;
};

/** Standalone affected counts for remove/keep or exit-placement picks. */
function affectedImpactForPick(pickId: string, picks: PickResult[]): AffectedImpactCounts | null {
  const found = picks.find((p) => p.pick_id === pickId);
  if (!found) return null;
  return {
    affected_trade_count: found.impact.affected_trade_count,
    affected_wins: found.impact.affected_wins,
    affected_losses: found.impact.affected_losses,
  };
}

export type PairwiseOverlapBreakdown = {
  pickALabel: string;
  pickBLabel: string;
  overlapPct: number;
  isExitPlacement: boolean;
  /** Trades affected if pick A runs by itself (removed or exits changed). */
  pickAStandaloneTrades: number;
  pickAStandaloneLosses: number;
  pickAStandaloneWins: number;
  /** Trades affected if pick B runs by itself (removed or exits changed). */
  pickBStandaloneTrades: number;
  pickBStandaloneLosses: number;
  pickBStandaloneWins: number;
  sharedTrades: number;
  sharedLosses: number;
  sharedWins: number;
  /** Set difference: A affected not also affected by B. */
  pickAExclusiveLosses: number;
  pickAExclusiveWins: number;
  /** Set difference: B affected not also affected by A. */
  pickBExclusiveLosses: number;
  pickBExclusiveWins: number;
  unionLosses: number;
  unionWins: number;
  unionTrades: number;
};

/** Human-readable W/L breakdown for one pairwise row. */
export function pairwiseOverlapBreakdown(
  row: PairwiseRow,
  picks: PickResult[]
): PairwiseOverlapBreakdown {
  const impactA = affectedImpactForPick(row.pick_a, picks);
  const impactB = affectedImpactForPick(row.pick_b, picks);
  const shared = row.overlap;

  const pickAExclusiveLosses = Math.max(0, (impactA?.affected_losses ?? 0) - shared.shared_losses);
  const pickAExclusiveWins = Math.max(0, (impactA?.affected_wins ?? 0) - shared.shared_wins);
  const pickBExclusiveLosses = Math.max(0, (impactB?.affected_losses ?? 0) - shared.shared_losses);
  const pickBExclusiveWins = Math.max(0, (impactB?.affected_wins ?? 0) - shared.shared_wins);

  return {
    pickALabel: pickLabelById(row.pick_a, picks),
    pickBLabel: pickLabelById(row.pick_b, picks),
    overlapPct: shared.overlap_pct_of_smaller_pick,
    isExitPlacement: pairwiseRowIsExitPlacement(row),
    pickAStandaloneTrades: impactA?.affected_trade_count ?? 0,
    pickAStandaloneLosses: impactA?.affected_losses ?? 0,
    pickAStandaloneWins: impactA?.affected_wins ?? 0,
    pickBStandaloneTrades: impactB?.affected_trade_count ?? 0,
    pickBStandaloneLosses: impactB?.affected_losses ?? 0,
    pickBStandaloneWins: impactB?.affected_wins ?? 0,
    sharedTrades: shared.shared_affected_count,
    sharedLosses: shared.shared_losses,
    sharedWins: shared.shared_wins,
    pickAExclusiveLosses,
    pickAExclusiveWins,
    pickBExclusiveLosses,
    pickBExclusiveWins,
    unionLosses: shared.union_losses,
    unionWins: shared.union_wins,
    unionTrades: shared.union_affected_count,
  };
}
